import { useQuery } from "@tanstack/react-query"
import { userService } from "../../services/userService"
import { User } from "../../Interfaces"
import { useContext, useEffect, useState } from "react"
import { roles } from "../../utils/roles"
import UiContext from "../../context/uiProvider"
import useUser from "../../hooks/useUser"
import { LARGE_TABLET, TABLET } from "../../utils/screenSize"
import DataContext from "../../context/dataProvider"
import Table from "./common/Table"
import PopupModal from "../../components/modals/PopupModal"
import { useQueryClient } from "@tanstack/react-query"

interface UsersTableProps {
  userProfileId?: number;
  defaultLimit?: number;
  customHeaders?: { label: string; key: string; to?: string }[];
  dataSource?: "user" | "location" | "organization";
  sourceId?: number;
  title?: string;
}

export default function UsersTable({
  userProfileId,
  defaultLimit = 10,
  customHeaders,
  dataSource = "user",
  sourceId,
  title = "Users"
}: UsersTableProps) {
  const { id } = useUser()
  const { deviceView } = useContext(UiContext)
  const { setUsers } = useContext(DataContext)
  const userId = userProfileId || id
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [showAddUsersModal, setShowAddUsersModal] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [availableUsers, setAvailableUsers] = useState<User[]>([])
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery<any>(
    ["users", dataSource, sourceId || userId, searchTerm],
    () => {
      switch (dataSource) {
        case "location":
          return userService.getAllByLocation(sourceId!);
        case "organization":
          return userService.getAllByOrganization(sourceId!);
        default:
          return userService.getAllByUser(userId);
      }
    },
    {
      cacheTime: 5000,
      suspense: true,
    }
  )

  const { data: allUsers, error: allUsersError } = useQuery<any>(
    ["all-users"],
    () => userService.getAllByUser(userId),
    {
      enabled: showAddUsersModal,
      retry: false,
      onError: (error) => {
        console.error("Error fetching users:", error);
      }
    }
  )

  useEffect(() => {
    if (allUsers && data) {
      // Filter out users that are already in the location/organization
      const currentUserIds = (data.users || data).map((user: User) => user.id)
      const available = (allUsers.users || allUsers).filter((user: User) => {
        // For organizations, only show users with roleId >= AREA_MANAGER (20)
        if (dataSource === "organization" && (user.roleId || 0) < 20) {
          return false
        }
        return !currentUserIds.includes(user.id)
      })
      setAvailableUsers(available)
    } else {
      setAvailableUsers([])
    }
  }, [allUsers, data, dataSource])

  const [filteredData, setFilteredData] = useState<User[] | null | undefined>(null)

  useEffect(() => {
    if (data) {
      const users = data.users || data
      setUsers((prev) => [...prev, ...users])
      const filtered = users
        .filter((user: User) =>
          searchTerm === "" ||
          (user.firstName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
          (user.lastName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
          (user.email?.toLowerCase() || "").includes(searchTerm.toLowerCase())
        )
        .map((user: User) => {
          user.roleName = roles[user.roleId || 1]
          user.userToLocation = user.userToLocation || []
          user.userToOrganization = user.userToOrganization || []
          return user
        })
      setFilteredData(filtered)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchTerm])

  const defaultHeaders = [
    { label: "First Name", key: "firstName", to: `/app/users` },
    { label: "Last Name", key: "lastName", to: `/app/users` },
  ]

  if (deviceView >= TABLET) {
    defaultHeaders.unshift({ label: "Id", key: "id", to: `/app/users` })
    defaultHeaders.push({ label: "Role", key: "roleName", to: `/app/users` })
  }

  if (deviceView >= LARGE_TABLET) {
    defaultHeaders.push(
      { label: "PIN", key: "pin", to: `/app/users` },
      { label: "Email", key: "email", to: `/app/users` }
    )
  }

  // Add delete column if we're showing location or organization users
  if (dataSource === "location" || dataSource === "organization") {
    defaultHeaders.push({ label: "Actions", key: "actions", to: "" })
  }

  const headers = customHeaders || defaultHeaders

  function handleSearch(query: string) {
    setSearchTerm(query)
  }

  const handleAddUsers = async () => {
    if (selectedUsers.length === 0) {
      alert("Please select at least one user to add");
      return;
    }

    if (!sourceId) {
      alert("Invalid source ID");
      return;
    }

    try {
      if (dataSource === "organization") {
        await userService.addUsersToOrganization(selectedUsers, sourceId);
      } else {
        await userService.addUsersToLocation(selectedUsers, sourceId);
      }
      setShowAddUsersModal(false);
      setSelectedUsers([]);
      queryClient.invalidateQueries(["users", dataSource, sourceId, searchTerm]);
    } catch (error) {
      console.error("Error adding users:", error);
      alert("Failed to add users. Please try again.");
    }
  };

  const handleRemoveUser = async (userId: number) => {
    if (sourceId) {
      try {
        if (dataSource === "organization") {
          await userService.removeUserFromOrganization(userId, sourceId);
        } else {
          await userService.removeUserFromLocation(userId, sourceId);
        }
        queryClient.invalidateQueries();
      } catch (error) {
        console.error("Error removing user:", error);
        alert("Failed to remove user. Please try again.");
      }
    }
  }

  if (!filteredData || isLoading) return null

  return (
    <>
      <Table
        data={filteredData}
        title={title}
        to={dataSource === "user" ? "/app/addUser" : undefined}
        handleSearch={handleSearch}
        headers={headers}
        showAddButton={dataSource === "location" || dataSource === "organization"}
        onAddClick={() => setShowAddUsersModal(true)}
        renderCell={(item, column) => {
          if (column.key === "actions" && (dataSource === "location" || dataSource === "organization")) {
            return (
              <button
                onClick={() => handleRemoveUser(item.id)}
                className="text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            );
          }
          return item[column.key];
        }}
      />
      {showAddUsersModal && (
        <PopupModal isOpen={showAddUsersModal} onCancel={() => setShowAddUsersModal(false)}>
          <div className="w-full flex flex-col p-4">
            <h2 className="text-xl font-bold mb-4">
              {dataSource === "organization" ? "Add Users to Area" : "Add Users to Location"}
            </h2>
            {allUsersError ? (
              <div className="text-red-500 p-4">
                {allUsersError instanceof Error ? allUsersError.message : "Error loading users. Please try again later."}
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {availableUsers.length === 0 ? (
                  <div className="p-4 text-gray-500">No users available to add.</div>
                ) : (
                  availableUsers.map((user) => (
                    <div key={user.id} className="flex items-center p-2 border-b">
                      <input
                        type="checkbox"
                        id={`user-${user.id}`}
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, user.id])
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== user.id))
                          }
                        }}
                        className="mr-2"
                      />
                      <label htmlFor={`user-${user.id}`} className="flex-1">
                        {user.firstName} {user.lastName} ({roles[user.roleId || 1]})
                      </label>
                    </div>
                  ))
                )}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowAddUsersModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 mr-2"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUsers}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Add Selected Users
              </button>
            </div>
          </div>
        </PopupModal>
      )}
    </>
  )
}

